import Axios from "axios";
import _, {debounce, isFunction} from "lodash";
import {initGridFooter} from "./footer";
import {initGridForInfiniteScrolling} from "./infiniteScrolling";
import {initGridForPagination} from "./pagination";
import {CustomNoRowsOverlay} from "./overlays/noRowsOverlay";
import createElement from "../createElement";

let defaultTableClasses = [
	'w-100',
	'flex-grow-1',
	'd-flex',
	'flex-column'
]

/**
 * Version Control: Last Edited 2024-05-21 - For ag-grid-community ^30.0.6 - Updated sort interceptors not working on touch devices
 */

/**
 * A configuration object used to initialize a grid
 * with the AG-grid api and custom properties and methods set
 *
 * @typedef {Object} GridInstanceCustom
 * @property {HTMLElement} _domElement - The DOM element containing the grid.
 * @property {array} _rows - An array containing the rows loaded in memory.
 * @property {function} _refresh - Refreshes the rows from the server, starting back at row 0
 * @property {number} _totalRows - The total number of rows available on the endpoint
 * @property {array} _sortModel - The currently active sort model
 * @property {object} _filters - The filters function or object passed initially
 * @property {() => Object.<string, *>} _getFilterValues Returns current filter values
 * @property {boolean} _initialized Is set to true when all custom methods has been set
 * @property {boolean} _autoRefresh flag for auto refreshing
 * @property {string} _autoRefreshStatus auto refresh status
 * @property {Element|null} _autoRefreshButtonContainer Container element for the auto refresh button
 * @property {number} _autoRefreshPeriod How often will auto refresh run in seconds
 * @property {function} _silentRefresh Refresh while preserving scroll position and no loader
 * **/


/**
 * A configuration object used to initialize a grid
 * with the AG-grid api and custom properties and methods set
 *
 * @typedef {import('ag-grid-community/dist/lib/entities/gridOptions').GridOptions & GridInstanceCustom} GridInstance
 */

// noinspection JSUnresolvedVariable
/**
 * A simple wrapper on AG Grid to set some default values
 * And initialize server side grids
 *
 * Note: The grid initializes asynchronously, use the onGridReady callback on the gridOptions.
 *
 * @param {HTMLElement|string} domElement - DOM element or selector where the grid will be initialized.
 * @param {import('ag-grid-community/dist/lib/entities/gridOptions').GridOptions | Object} gridOptions - AG Grid options object, default values are merged onto it.
 * @param {string|array|function} source - Can be
 *                * A string pointing to an endpoint to retrieve rows
 *                * An array of data objects for a fully local grid - Partial support only, no filtering/sorting/searching
 *                * A function returning an array of objects, same behavior as above
 * @param {Object} filters - Can be
 *              * An object containing externals filters in the form of key => getterFunction.
 *                The key and its value gets send on every row request
 *              * A function that returns an object with the final filter values, that object is then
 *                merged with the default filters
 *
 * @returns {GridInstance} The grid options object used to create the grid, with the api property set.
 */
function createGrid(domElement, gridOptions, source, filters = {}) {
	let ignoreInitialRowDataChangedEvent = true;

	/** Intercept gridOptions that clash with defaults */

		// Intercept onRowDataUpdated event listener
	let customOnRowDataUpdated = null;
	if (gridOptions.onRowDataUpdated) {
		customOnRowDataUpdated = gridOptions.onRowDataUpdated
		delete gridOptions.onRowDataUpdated;
	}

	/** @type {GridInstance|Object} */
	let finalOptions;

	// noinspection JSUnusedGlobalSymbols
	const defaultOptions = {
		defaultColDef: {
			flex: 1,
			minWidth: 160,
			suppressMovable: true,
		},
		pagination: true,
		suppressMultiSort: true,
		enableCellTextSelection: true,
		ensureDomOrder: true,
		paginationPageSize: 50,
		cacheBlockSize: 100,

		enableBrowserTooltips: true,

		rowBuffer: 9999999,

		// Overlays
		noRowsOverlayComponent: CustomNoRowsOverlay,
		noRowsOverlayComponentParams: {
			// Send the final grid instance container as a parameter
			getInstance: () => {
				return finalOptions;
			}
		},

		onRowDataUpdated(event) {
			// Ignore the first event as the row its being initialized
			if (ignoreInitialRowDataChangedEvent) {
				ignoreInitialRowDataChangedEvent = false;
				return;
			}

			if (this._updateRowWidths) {
				this._updateRowWidths();
			}
			this._showViewport();
			setupGridSortInterceptors(this);

			if (customOnRowDataUpdated && _.isFunction(customOnRowDataUpdated)) {
				// noinspection JSValidateTypes
				customOnRowDataUpdated(event);
			}

			// Set up timer for next refresh
			if (this._autoRefreshStatus === 'on') {
				setTimeout(
					() => this._handleAutoRefresh(),
					this._autoRefreshPeriod * 1000 // Turn to ms
				)
			}
		},

		// Custom properties
		_autoRefresh: false,
		_afterSuccessCallback: null,
		_autoRefreshPeriod: 10,
	}

	finalOptions = {
		...defaultOptions,
		...gridOptions
	};

	// Find DOM element if a string was passed
	if (domElement instanceof String || typeof (domElement) == 'string') {
		domElement = document.querySelector(domElement);
		if (!domElement) {
			throw 'DOM Element not found';
		}
	}

	// Set ag grid theme
	domElement.classList.add('ag-theme-alpine');

	// Set default classes on table dom element
	defaultTableClasses.forEach(className => {
		domElement.classList.add(className);
	})

	// Set header height if compact flag
	if (domElement.classList.contains('compact')) {
		finalOptions.headerHeight = 32;
	}

	// Set data source
	finalOptions.rowModelType = 'clientSide';
	finalOptions.rowData = [];

	// Initialize search input
	initSearchInput(domElement, finalOptions);


	// Remove custom properties so the grid doesn't complain about it
	delete finalOptions._afterSuccessCallback;
	delete finalOptions._rowName;

	// Get initial sort model and delete sort from columns as we don't want to use the grid's
	let initialSortModel = [];
	finalOptions.columnDefs.forEach(col => {
		if (col.sort) {
			initialSortModel[0] = {
				'colId': col.field,
				'sort': col.sort,
			}
			delete col.sort;
		}
	})

	// Intercept pagination call and remove it
	let usePagination = false;

	if (finalOptions.pagination) {
		usePagination = true;
		finalOptions.pagination = false;
	}

	finalOptions._initialized = false;

	// Initialize grid
	if (usePagination) {
		initGridForPagination(domElement, finalOptions);
	} else {
		initGridForInfiniteScrolling(domElement, finalOptions);
	}

	// Add back custom properties passed by the user
	// noinspection JSUnresolvedVariable
	finalOptions._afterSuccessCallback = gridOptions._afterSuccessCallback
	// noinspection JSUnresolvedVariable
	finalOptions._rowName = gridOptions._rowName

	// Add custom properties
	finalOptions._domElement = domElement;
	finalOptions._rows = [];
	finalOptions._totalRows = 0;
	finalOptions._rowsEndpoint = source;
	finalOptions._sortModel = initialSortModel;
	finalOptions._filters = filters;
	finalOptions._autoRefreshContainer = null;
	finalOptions._autoRefreshStatus = finalOptions._autoRefresh ? 'on' : 'off';

	// Add custom methods
	finalOptions._getFilterValues = getFilterValues;
	finalOptions._getRows = getRows;
	finalOptions._handleSortChange = handleSortChange;
	finalOptions._updateHeadersSortState = updateHeadersSortState;
	finalOptions._hideViewport = hideViewport;
	finalOptions._showViewport = showViewport;
	finalOptions._renderAutoRefreshButton = renderAutoRefreshButton;
	finalOptions._turnOnAutoRefresh = turnOnAutoRefresh;
	finalOptions._turnOffAutoRefresh = turnOffAutoRefresh;
	finalOptions._handleAutoRefresh = handleAutoRefresh;
	finalOptions._silentRefresh = silentRefresh;

	// Setup footer
	initGridFooter(finalOptions);

	// Initialize auto refresh
	initAutoRefreshButton(domElement, finalOptions);

	finalOptions._initialized = true;

	// Start grid
	finalOptions._refresh();

	// Update headers in case a default sort was set
	finalOptions._updateHeadersSortState();

	return finalOptions;
}

/**
 * Get rows from the endpoint
 */
function getRows(startRow, endRow = null) {
	if (!endRow) {
		endRow = startRow + this.paginationPageSize;
	}

	const sortModel = this._sortModel;
	const filterModel = [];
	const filterValues = this._getFilterValues();

	return new Promise((resolve, reject) => {

		if (typeof this._rowsEndpoint === 'string') {
			Axios.post(this._rowsEndpoint, {
				startRow,
				endRow,
				sortModel,
				filterModel,
				filterValues
			}).then(response => {
				resolve(response.data);
			}).catch(reject)
		} else if (Array.isArray(this._rowsEndpoint)) {
			resolve(
				getRowsFromLocalDataSource(
					this._rowsEndpoint,
					startRow,
					endRow,
					sortModel,
					filterModel,
					filterValues
				)
			);
		} else if (typeof (this._rowsEndpoint) === 'function') {
			resolve(
				getRowsFromLocalDataSource(
					this._rowsEndpoint(),
					startRow,
					endRow,
					sortModel,
					filterModel,
					filterValues
				)
			);
		} else {
			throw 'Unsupported source type';
		}
	})
}

/**
 *
 * @param {array} data
 * @param {number} startRow
 * @param {number|null} endRow
 * @param {array} sortModel
 * @param {any} filterModel
 * @param {any} filterValues
 */
function getRowsFromLocalDataSource(
	data,
	startRow,
	endRow,
	sortModel,
	filterModel,
	filterValues
) {
	const count = data.length;

	return {
		rows: data.slice(startRow, endRow),
		lastRowIndex: count,
	}
}

function getFilterValuesFromObject(filters) {
	const filterValues = {};

	Object.keys(filters).forEach(key => {
		const value = filters[key]();

		if (value) {
			filterValues[key] = value;
		}
	})

	return filterValues;
}

/**
 * This method replaces ag grids handler for sorting columns
 * to use the server instead of doing it in place
 *
 * @param header
 */
function handleSortChange(header) {

	const columnId = header.getAttribute('col-id');

	let iconContainer = header.querySelector('.ag-cell-label-container');

	let sort = iconContainer.classList.contains('ag-header-cell-sorted-none') ? 'none' :
		(iconContainer.classList.contains('ag-header-cell-sorted-asc') ? 'ascending' : 'descending')

	switch (sort) {
		case 'none':
			this._sortModel = [{
				'colId': columnId,
				'sort': 'asc',
			}]
			break;
		case 'ascending':
			this._sortModel = [{
				'colId': columnId,
				'sort': 'desc',
			}]
			break;
		case 'descending':
			this._sortModel = []
			break;
	}

	this._updateHeadersSortState();
	this._refresh();
}

/**
 * Updates each sortable header state based on the
 * currently active sort model
 */
function updateHeadersSortState() {
	// Clean up all headers
	this._domElement.querySelectorAll('.ag-header-cell-sortable').forEach(header => {
		const iconContainer = header.querySelector('.ag-cell-label-container');
		const ascendingIcon = iconContainer.querySelector('.ag-sort-ascending-icon');
		const descendingIcon = iconContainer.querySelector('.ag-sort-descending-icon');

		// Hide icons
		ascendingIcon.classList.add('ag-hidden');
		descendingIcon.classList.add('ag-hidden');

		// Set icon container class to its default state
		iconContainer.classList.remove('ag-header-cell-sorted-desc');
		iconContainer.classList.remove('ag-header-cell-sorted-asc');
		iconContainer.classList.add('ag-header-cell-sorted-none');
	})

	// Set header for current sort model
	const sortModel = this._sortModel[0];
	if (sortModel) {
		const sort = sortModel.sort;

		const header = this._domElement.querySelector(
			`.ag-header-cell-sortable[col-id="${sortModel.colId}"]`
		)
		const iconContainer = header.querySelector('.ag-cell-label-container');
		const ascendingIcon = iconContainer.querySelector('.ag-sort-ascending-icon');
		const descendingIcon = iconContainer.querySelector('.ag-sort-descending-icon');

		switch (sort) {
			case 'asc':
				header.ariaSort = 'ascending';
				iconContainer.classList.remove('ag-header-cell-sorted-none');
				iconContainer.classList.add('ag-header-cell-sorted-asc');
				ascendingIcon.classList.remove('ag-hidden');
				break;
			case 'desc':
				header.ariaSort = 'descending';
				iconContainer.classList.remove('ag-header-cell-sorted-asc');
				iconContainer.classList.remove('ag-header-cell-sorted-none');
				iconContainer.classList.add('ag-header-cell-sorted-desc');
				descendingIcon.classList.remove('ag-hidden');
				break;
		}
	}
}

/**
 * Search for a nearby search input on the same card and pulls its value
 */
function getSearchTerm(domElement) {
	// Are we inside a card?
	let parentContainer = $(domElement).closest('.card')[0];
	if (!parentContainer) {
		// Are we inside a modal?
		parentContainer = $(domElement).closest('.modal')[0];
	}

	let searchTerm = '';

	if (parentContainer) {
		const searchInput = parentContainer.querySelector('input[name=search]');
		if (searchInput) {
			searchTerm = searchInput.value;
		}
	}

	return searchTerm;
}

/**
 * Initializes grid search input if present
 *
 * It will try to find an input with the name search on the card containing the grid
 */
function initSearchInput(domElement, gridOptions) {
	// Are we inside a card?
	let parentContainer = $(domElement).closest('.card')[0];
	if (!parentContainer) {
		// Are we inside a modal?
		parentContainer = $(domElement).closest('.modal')[0];
	}

	// If we are inside something standard, search for input
	if (parentContainer) {
		const searchInput = parentContainer.querySelector('input[name=search]');
		if (searchInput) {
			const debouncedSearch = debounce(() => {
				if (gridOptions.api) {
					if (searchInput.value !== gridOptions._lastSearchInputValue) {
						gridOptions._lastSearchInputValue = searchInput.value;
						gridOptions._refresh();
						gridOptions._turnOffAutoRefresh();
					}
				}
			}, 256, {
				trailing: true
			});

			// noinspection JSCheckFunctionSignatures
			searchInput.addEventListener('keydown', debouncedSearch);
		}
	}
}

function initAutoRefreshButton(domElement, gridOptions) {
	const containerCard = $(domElement).closest('.card')[0];
	if (gridOptions._autoRefresh && containerCard) {
		const cardTools = containerCard.querySelector('.card-tools');

		// Add auto refresh container
		const buttonContainer = createElement(`<div class="auto-refresh-button-container"></div>`);

		buttonContainer.addEventListener('click', () => {
			gridOptions._refresh();
			gridOptions._turnOnAutoRefresh();
		})

		cardTools.prepend(buttonContainer);
		gridOptions._autoRefreshButtonContainer = buttonContainer;
		gridOptions._turnOnAutoRefresh();
	}
}

function renderAutoRefreshButton() {
	const container = this._autoRefreshButtonContainer;
	if (container) {
		if (this._autoRefreshStatus === 'on') {
			container.title = 'Auto Refresh Enabled'
			container.innerHTML = `
				<i class="fa-solid fa-arrows-rotate"></i>
				<span class="text on">AUTO</span>
			`;
		} else {
			container.title = 'Auto Refresh Disabled'
			container.innerHTML = `
				<i class="fa-solid fa-arrows-rotate"></i>
				<span class="text off">OFF</span>
			`;
		}
	}
}

function silentRefresh() {
	this._skipLoadingOverlay = true;
	// Preserve scrolls position
	const viewPort = this._domElement.querySelector('.ag-body-viewport')
	this._preservedXScroll = viewPort.scrollLeft;
	this._preservedYScroll = viewPort.scrollTop;

	this._refresh();
	this._skipLoadingOverlay = false;
}

function turnOnAutoRefresh() {
	this._autoRefreshStatus = 'on';
	this._renderAutoRefreshButton();
}

function turnOffAutoRefresh() {
	this._autoRefreshStatus = 'off';
	this._renderAutoRefreshButton();
}

function handleAutoRefresh() {
	if (this._autoRefreshStatus === 'on') {
		this._silentRefresh();
	}
}

/**
 * Returns an object filled with default filter
 * values and user provided filter values
 */
function getFilterValues() {
	const defaultFilters = {
		search: () => getSearchTerm(this._domElement),
	}

	const filters = this._filters;

	if (isFunction(filters)) {
		// If filters is a function we assume it returns an object with the filter
		// values, so we will merge it with default filters and return that
		const userFilterValues = filters();

		const defaultFilterValues = getFilterValuesFromObject(defaultFilters);

		return {
			...defaultFilterValues,
			...userFilterValues
		};
	} else if (filters instanceof Object) {
		// filters is an object, so we treat each
		// entry as filterKey => getter()
		const finalFilters = {
			...defaultFilters,
			...filters
		}

		return getFilterValuesFromObject(finalFilters);
	}
}

function setupGridSortInterceptors(finalOptions) {
	/** Setup sorting */
		// Because there are multiple events on the headers
		// the sort interceptor will be called multiple times,
		// so we create a throttled function to handle it
	const handleSortChangeCaller = _.throttle(header => {
			// Call our handler
			finalOptions._handleSortChange(header);
		}, 64, {trailing: false});

	// Intercept the grid's sort handlers
	finalOptions._domElement.querySelectorAll('.ag-header-cell-sortable').forEach(header => {
		// Only intercept the header the first time
		if (header.dataset._intercepted !== 'true') {
			header.dataset._intercepted = 'true';
			header.addEventListener('click', e => {
				// Stop the event from going forward and
				// triggering AG grid default behavior
				e.stopPropagation();

				// noinspection JSValidateTypes
				handleSortChangeCaller(header);
			}, true) // Capture the event

			// To support touch devices we use pointerdown event
			header.addEventListener('pointerdown', e => {
				// Stop the event from going forward and
				// triggering AG grid default behavior
				e.stopPropagation();

				// noinspection JSValidateTypes
				handleSortChangeCaller(header);
			}, true) // Capture the event
		}
	})
}

function hideViewport() {
	this._domElement.querySelector('.ag-body-viewport').style.opacity = 0;
}

function showViewport() {
	this._domElement.querySelector('.ag-body-viewport').style.opacity = 1;
}

// noinspection JSUnusedGlobalSymbols
function setGridDefaultClasses(classArray) {
	defaultTableClasses = classArray;
}

// noinspection JSUnusedLocalSymbols
const LOCALE_ES = {
	more: 'Mas',
	to: 'hasta',
	of: 'de',
	page: 'pagina',

	loadingOoo: 'Cargando',
	noRowsToShow: 'No Hay Nada Que Mostrar',
}

export {
	createGrid,
	setGridDefaultClasses
}
